import { Autocomplete, SxProps, TextField, Theme } from '@mui/material';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { SelectOption } from '../../../models/form';

interface HeiAutocompleteProps<TForm extends FieldValues> {
    control: Control<TForm>;
    name: Path<TForm>;
    label: string;
    placeholder?: string;
    sx?: SxProps<Theme>;
    rules?: Omit<
        RegisterOptions<TForm>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
    options: SelectOption[];
    disabled?: boolean;
}

const HeiAutocomplete = <TForm extends FieldValues>({
    control,
    name,
    label,
    placeholder,
    sx,
    options,
    rules,
    disabled,
}: HeiAutocompleteProps<TForm>): JSX.Element => {
    const required = rules?.required;
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ fieldState, field: { ref, onChange, ...field } }) => {
                const error = fieldState.error;
                const errorMessage = error?.message;

                const selectedOption =
                    options.find((option) => option.value === field.value) ?? null;

                return (
                    <Autocomplete
                        disabled={disabled}
                        onChange={(_, data) => onChange(data?.value)}
                        options={options}
                        value={selectedOption}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                inputRef={ref}
                                name={field.name}
                                label={label}
                                onBlur={field.onBlur}
                                placeholder={placeholder}
                                required={!!required}
                                InputLabelProps={{ shrink: true }}
                                sx={sx || { width: '45%' }}
                                variant='outlined'
                                error={error !== undefined}
                                helperText={errorMessage}
                            />
                        )}
                    />
                );
            }}
        />
    );
};

export default HeiAutocomplete;
